/** Shared defaults for Lucide animated icons (editor + SSR + HTML export). */

import type { ElementNode } from "../types";

/** True if the document has at least one library-mode Lucide animated icon (needs client hydration on public pages). */
export function builderDocContainsLucideAnimatedLibrary(
	nodes: ElementNode[] | undefined
): boolean {
	if (!nodes?.length) {
		return false;
	}
	for (const n of nodes) {
		if (n.type === "lucide_animated_icon") {
			const p = (n.props ?? {}) as { mode?: string };
			if (p.mode !== "local") {
				return true;
			}
		}
		if (n.type === "button") {
			const p = (n.props ?? {}) as {
				buttonIconKind?: string;
				iconLucideMode?: string;
			};
			if (
				p.buttonIconKind === "lucide_animated" &&
				p.iconLucideMode !== "local"
			) {
				return true;
			}
		}
		if (
			"children" in n &&
			Array.isArray(n.children) &&
			builderDocContainsLucideAnimatedLibrary(n.children)
		) {
			return true;
		}
	}
	return false;
}

/** Safe `document.getElementById` target for delegated hover/click (canvas). */
export function sanitizeAnimationHostDomId(
	raw: string | undefined
): string | undefined {
	const s = typeof raw === "string" ? raw.trim() : "";
	if (!s) {
		return undefined;
	}
	// HTML id: conservative charset (avoids spaces, quotes, script injection).
	if (!/^[A-Za-z][\w.-]*$/.test(s)) {
		return undefined;
	}
	return s;
}

export function resolveLucideAnimatedPrimaryColor(
	primaryColor: string | undefined,
	styleColor: unknown
): string {
	const p = typeof primaryColor === "string" ? primaryColor.trim() : "";
	if (p) {
		return p;
	}
	if (typeof styleColor === "string" && styleColor.trim()) {
		return styleColor.trim();
	}
	return "#0f172a";
}

export function resolveLucideAnimatedSecondaryColor(
	secondaryColor: string | undefined
): string | undefined {
	const s = typeof secondaryColor === "string" ? secondaryColor.trim() : "";
	return s || undefined;
}

export function resolveLucideAnimatedStrokeWidth(
	strokeWidth: string | undefined
): number {
	const n = Number.parseFloat(String(strokeWidth ?? "2").trim());
	if (!Number.isFinite(n) || n <= 0) {
		return 2;
	}
	return Math.min(4, Math.max(0.5, n));
}

export function resolveLucideAnimatedPlayOn(
	playOn: string | undefined
): "hover" | "click" {
	return playOn === "click" ? "click" : "hover";
}
